import client from 'api/client';
import { limpiarEstadosSesion } from 'hooks/useEstadoSesion';

export interface LoginResponse {
  token: string;
  user: {
    keyUser: string;
    title: string | null;   // db/127
    name: string;
    firstLastName: string;
    email: string;
    idProfile: number | null;
    keyProfile: string | null;
    profileName: string | null;
    isSuperadmin: boolean;
    permissions: string[];
  };
}

export interface CheckResponse {
  keyUser: string;
  isSuperadmin: boolean;
  permissions: string[];
  keyProfile?: string | null;
  title?: string | null;
  /**
   * Emulación en curso según el SERVIDOR, que lee el claim del token. Es la
   * fuente buena: lo que el navegador tenga guardado puede estar perdido o
   * borrado, y la franja de aviso no puede depender de eso.
   */
  impersonatedBy?: { name: string | null } | null;
}

/** Un usuario al que el superadmin puede emular. */
export interface ImpersonableUser {
  keyUser: string;
  name: string;
  email: string;
  position: string | null;
  profileName: string | null;
  keyProfile: string | null;
}

/**
 * Sesión que devuelven entrar y salir de una emulación.
 *
 * Misma forma que LoginResponse a propósito —el store y la tabla de inicio por
 * perfil se reusan tal cual— más quién está detrás.
 */
export interface ImpersonationSession extends LoginResponse {
  impersonatedBy: { keyUser: string; name: string; email: string } | null;
}

export interface ProfileData {
  keyUser: string;
  name: string;
  firstLastName: string;
  secondLastName: string | null;
  phone: string | null;
  email: string;
  position: string | null;
}

export interface UpdateProfilePayload {
  name: string;
  firstLastName: string;
  secondLastName?: string;
  phone?: string;
  position?: string;
}

export class AuthService {
  static login = async (email: string, password: string): Promise<LoginResponse> => {
    limpiarEstadosSesion();   // los filtros guardados son de quien estaba (useEstadoSesion)
    const res = await client.post<{ data: LoginResponse }>(`/auth/login/${Date.now()}`, {
      email,
      password
    });
    return res.data.data;
  };

  static check = async (): Promise<CheckResponse> => {
    const res = await client.post<{ data: CheckResponse }>(`/auth/check/${Date.now()}`);
    return res.data.data;
  };

  static logout = async (): Promise<void> => {
    limpiarEstadosSesion();   // los filtros guardados son de quien estaba (useEstadoSesion)
    await client.post(`/auth/logout/${Date.now()}`);
  };

  // ── Emulación de usuario (solo superadmin) ────────────────────────────────
  //
  // El servidor vuelve a comprobar quién pide en las tres rutas; esconder el
  // botón en el encabezado es comodidad, no la protección.

  static impersonationUsers = async (): Promise<ImpersonableUser[]> => {
    const res = await client.post<{ data: { users: ImpersonableUser[] } }>(
      `/auth/impersonation/users/${Date.now()}`
    );
    return res.data.data.users;
  };

  static impersonate = async (keyUser: string): Promise<ImpersonationSession> => {
    limpiarEstadosSesion();   // los filtros guardados son de quien estaba (useEstadoSesion)
    const res = await client.post<{ data: ImpersonationSession }>(
      `/auth/impersonation/start/${Date.now()}`, { keyUser }
    );
    return res.data.data;
  };

  static stopImpersonation = async (): Promise<ImpersonationSession> => {
    limpiarEstadosSesion();   // los filtros guardados son de quien estaba (useEstadoSesion)
    const res = await client.post<{ data: ImpersonationSession }>(
      `/auth/impersonation/stop/${Date.now()}`
    );
    return res.data.data;
  };

  static refresh = async (): Promise<{ token: string }> => {
    const res = await client.post<{ data: { token: string } }>(`/auth/refresh/${Date.now()}`);
    return res.data.data;
  };

  static changeMyPassword = async (currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> => {
    await client.post(`/auth/change-my-password/${Date.now()}`, {
      currentPassword,
      newPassword,
      confirmPassword,
    });
  };

  static getProfile = async (): Promise<ProfileData> => {
    const res = await client.post<{ data: ProfileData }>(`/auth/profile/${Date.now()}`);
    return res.data.data;
  };

  static updateProfile = async (payload: UpdateProfilePayload): Promise<{ name: string; firstLastName: string }> => {
    const res = await client.post<{ data: { name: string; firstLastName: string } }>(`/auth/update-profile/${Date.now()}`, payload);
    return res.data.data;
  };

  static getLastLogout = async (keyUser: string): Promise<string | null> => {
    const res = await client.post<{ data: { lastLogout: string | null } }>(`/sessions/my-last-logout/${Date.now()}`, { keyUser });
    return res.data.data.lastLogout;
  };

  static forgotPassword = async (email: string): Promise<void> => {
    await client.post(`/auth/forgot/${Date.now()}`, { email });
  };

  static validateResetToken = async (token: string): Promise<boolean> => {
    const res = await client.post<{ data: { valid: boolean } }>(`/auth/validate-token/${Date.now()}`, { token });
    return res.data.data.valid;
  };

  static resetPassword = async (token: string, password: string, confirmPassword: string): Promise<void> => {
    await client.post(`/auth/change-password/${Date.now()}`, { token, password, confirmPassword });
  };
}
